import * as three from 'three';
import { Array2D, ClientData, Map2D, MapND, mod } from '@kosmod/kit';
import {
  Mini, MiniAnimation, MiniID, MiniTemplate, MiniTemplateID, PhysicsDice,
  PhysicsDiceID, Surface, SurfaceID, TerrainCanvas, TerrainCanvasID, TerrainChunk,
  TerrainStroke,
  TheatrePosition, ToolStateRaw
} from '../defs';
import { Game, Player, PlayerID } from '@kosmod/sesame';
import { AnyLogicState } from '../defs/logic/all';
import { CanvasCoordinator, ReadonlyChunkMap } from '@kosmod/stew';
import { DiceTemplate, DiceTemplateID, Die } from '@kosmod/andy';
import { K3D } from '@kosmod/graphics-3d';
import { TerrainBrush, TerrainBrushID } from '../defs/terrainBrush';
import { MiniModel, MiniModelID, MiniSprite, MiniSpriteID } from '../defs/miniRender';

export const TerrainChunkState = {
  /** There is nothing here, render an empty space */
  EMPTY: 0,
  /** There is a thing here, render something */
  ADD: 1,
  /**
   * Like Empty, but if this chunk is being added
   * to another chunk, remove something if something was here before
   * */
  NEGATE: 2,
  /**
   * Don't render anything here, but pretend to your neighbors that
   * this space is occupied.
   * */
  GHOST: 2,
} as const;
export type TerrainChunkState = typeof TerrainChunkState[keyof typeof TerrainChunkState];

export type TerrainChunk2 = {
  position: three.Vector2,
  data: Array2D<TerrainChunkState>,
  version: symbol,
}

export type TheatreController = {
  addCanvas(surface: SurfaceID, palette: TerrainBrushID[] | null): void,
  
  // addTerrain(canvas: TerrainCanvasID, box: three.Box2): void,
  addPhysicsDie(diceTemplateId: DiceTemplateID, initialTransform: K3D.Transform): void,
  removePhysicsDie(diceId: PhysicsDiceID): void,

  paint(...strokes: TerrainStroke[]): void,

  //addGrid(box: three.Box2): void,

  addMini(position: TheatrePosition, logic: AnyLogicState[]): void,
  moveMini(miniId: MiniID, newPosition: TheatrePosition, animation?: null | MiniAnimation): void,
  setMiniLogic(miniId: MiniID, logicIndex: number, logicState: AnyLogicState): void,
  removeMini(miniId: MiniID): void,

  sync(cursor: TheatrePosition | null, dice: null | [PhysicsDiceID, K3D.Transform][]): void,
  setTool(tool: ToolStateRaw | null): void,


  refresh(): void,
};

export type TheatreGridChunk = {
  position: three.Vector2,
  size: three.Vector2,

  gridData: boolean[][],

  version: number,
}

/**
 * All the runtime data associated with a Scene, typically
 * synchronised over the network and manipulated via the TheatreController
 */
export type TheatreData = {
  self: Player,
  game: Game,

  // terrain stuff
  canvasCoordinator: CanvasCoordinator,
  chunks: MapND<[canvas: TerrainCanvasID, x: number, y: number], TerrainChunk>,
  canvases: TerrainCanvas[],
  surfaces: Surface[],
  defaultSurfaceId: null | SurfaceID,

  players: { id: PlayerID, cursor: ClientData<TheatrePosition | null>, tool: null | ToolStateRaw }[],
  minis: ClientData<Mini>[],

  // TODO: move
  // These should be managed by the state
  // walkable: ReadonlyChunkMap<WalkableNode>,
  // interior: ReadonlyChunkMap<InteriorTile>,
  // visibility: ReadonlyChunkMap<number>,

  /**
   * 
   */
  brushes: Map<TerrainBrushID, TerrainBrush>,
  miniTemplates: Map<MiniTemplateID, MiniTemplate>,
  diceTemplates: Map<DiceTemplateID, DiceTemplate>,
  miniModels: Map<MiniModelID, MiniModel>,
  miniSprites: Map<MiniSpriteID, MiniSprite>,

  remotePhysicsDice: PhysicsDice[],
  remotePhysicsDieTransforms: Map<PhysicsDiceID, K3D.Transform>,
  ownedPhysicsDice: PhysicsDice[],

  ready: boolean,
  contentReady: boolean,
}

export const getChunkKey = (chunk: TheatreGridChunk) => `${chunk.position.x},${chunk.position.y}`

const DEFAULT_CHUNK_SIZE = 8;
const createChunkData = () => Array.from({ length: DEFAULT_CHUNK_SIZE })
  .map(() =>  Array.from({ length: DEFAULT_CHUNK_SIZE }).map(() => false));

const DEFAULT_CHUNK = createChunkData();
DEFAULT_CHUNK[0][0] = true;
DEFAULT_CHUNK[0][1] = true;
// DEFAULT_CHUNK[1][0] = true;

export const readTerrainData = (x: number, y: number, chunkMap: Map2D<number, number, TerrainChunk2>) => {
  const cx = Math.floor(x/8);
  const cy = Math.floor(y/8);

  if (!chunkMap.has(cx, cy))
    return TerrainChunkState.EMPTY;

  const chunk = chunkMap.get(cx, cy);
  return chunk.data.get(mod(x, 8), mod(y, 8));
}

export function* integerCoordinates(box: three.Box2) {
  const vector = new three.Vector2();
  for (let x = box.min.x; x < box.max.x; x++) {
    for (let y = box.min.y; y < box.max.y; y++) {
      vector.x = x;
      vector.y = y;
      yield vector;
    }
  }
}